import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { getStoredApiUrl, isAllowedApiUrl, saveRuntimeApiUrl } from '../api/config';
import { queryClient } from '../api/queryClient';
import { buildLogoutUrl, getCognitoConfig } from '../auth/cognitoConfig';
import { BILLABLE_LOCK_EVENT } from '../hooks/useBillableNavigationLock';
import { setActiveOperatorIdentity } from '../utils/operatorSession';
import BrandMark from './BrandMark';
import { SessionActionProvider } from './SessionActionContext';

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M8 12h8" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Left dark brand panel — constant across the loading and sign-in states. */
function BrandPanel() {
  return (
    <div className="auth-brand">
      <svg className="auth-watermark" viewBox="0 0 100 100" width="560" height="560" aria-hidden="true">
        <path d="M14 30 L86 30 L86 74 L14 74 Z" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M14 30 L50 57 L86 30" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinejoin="round" />
      </svg>
      <div className="auth-lockup">
        <BrandMark size={30} />
        <span className="brand-word">FIFTY 410</span>
      </div>
      <div className="auth-center">
        <div className="auth-eyebrow">Operations Console</div>
        <h2 className="auth-headline">Ship, track, and reconcile FedEx labels from one console.</h2>
        <p className="auth-support">
          Purchases are confirmed, retry-safe, and auditable. Tracking syncs continuously from the
          Worker.
        </p>
      </div>
      <div className="auth-brand-meta">
        <span>SOC 2 Type II</span>
        <span className="sep" />
        <span>v2.4.1</span>
        <span className="sep" />
        <span className="ok">
          <span className="dot" />
          All systems normal
        </span>
      </div>
    </div>
  );
}

/** Double-panel shell: dark brand panel + light form card. */
function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <BrandPanel />
      <div className="auth-form">
        <div className="auth-form-col">
          <div className="auth-card">
            <div className="auth-strip" />
            <div className="auth-body">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Single sign-on gate. Renders the app once the operator has a valid Cognito
 * session (obtained via Google), otherwise shows the two-panel "Sign in to your
 * workspace" screen. The authenticated branch publishes the operator identity +
 * sign-out to the app shell via SessionActionProvider.
 */
function SsoGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const authenticatedSubject =
    auth.isAuthenticated && typeof auth.user?.profile.sub === 'string'
      ? auth.user.profile.sub
      : null;
  // Publish only the subject from the verified OIDC session. Child pages use it
  // synchronously to namespace billable recovery data in sessionStorage.
  setActiveOperatorIdentity(authenticatedSubject);
  const [apiUrl, setApiUrl] = useState(getStoredApiUrl());
  const [apiUrlReady, setApiUrlReady] = useState(() => isAllowedApiUrl(getStoredApiUrl()));
  const [showUrlForm, setShowUrlForm] = useState(() => !isAllowedApiUrl(getStoredApiUrl()));
  const [urlError, setUrlError] = useState('');
  const [billableLocked, setBillableLocked] = useState(false);

  useEffect(() => {
    const handleBillableLock = (event: Event) =>
      setBillableLocked(Boolean((event as CustomEvent<boolean>).detail));
    window.addEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
    return () => window.removeEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
  }, []);

  const handleSaveUrl = (event: FormEvent) => {
    event.preventDefault();
    const normalized = apiUrl.trim().replace(/\/+$/, '');
    if (!isAllowedApiUrl(normalized)) {
      setUrlError(
        import.meta.env.DEV
          ? 'Enter a valid HTTP or HTTPS Worker URL, or leave it blank for the local proxy.'
          : 'Enter a trusted HTTPS Worker URL configured for this build.'
      );
      return;
    }
    try {
      saveRuntimeApiUrl(normalized);
    } catch {
      setUrlError('Session storage is unavailable. Enable it for this site and try again.');
      return;
    }
    setUrlError('');
    setApiUrlReady(true);
    setShowUrlForm(false);
  };

  const handleSignIn = () => {
    setUrlError('');
    void auth.signinRedirect();
  };

  const handleSignOut = () => {
    const config = getCognitoConfig();
    const logoutUrl = config ? buildLogoutUrl(config) : null;
    // Drop every cached response before the session ends so the next operator
    // to sign in on this tab never sees this user's data. (The Hosted UI
    // redirect below usually discards the page anyway, but there is no logout
    // URL when no Hosted UI is configured.)
    queryClient.clear();
    setActiveOperatorIdentity(null);
    void auth.removeUser().finally(() => {
      if (logoutUrl) window.location.assign(logoutUrl);
    });
  };

  if (auth.isLoading) {
    return (
      <AuthShell>
        <h1>
          <span className="auth-spinner" aria-hidden="true" />
          Signing you in…
        </h1>
        <p className="auth-lead">Completing single sign-on for this workspace.</p>
      </AuthShell>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <AuthShell>
        <h1>Sign in to your workspace</h1>
        <p className="auth-lead">
          Access is limited to authorized Fifty 410 operators. Single sign-on is enforced.
        </p>

        <div className="auth-workspace">
          <BrandMark size={24} />
          <div className="auth-workspace-meta">
            <div className="auth-workspace-name">Fifty 410</div>
            <div className="auth-workspace-url">shipping.fifty410.com</div>
          </div>
          <span className="auth-prod">
            <span className="dot" />
            Production
          </span>
        </div>

        {(auth.error || urlError) && (
          <div role="alert" className="auth-error">
            {urlError || auth.error?.message}
          </div>
        )}

        <button type="button" className="auth-google" onClick={handleSignIn} disabled={!apiUrlReady}>
          <GoogleIcon />
          Continue with Google
        </button>

        <div className="auth-or">
          <span className="line" />
          <span className="word">or</span>
          <span className="line" />
        </div>

        {/* Advanced option: SSO builds authenticate with Google only — this
            just points the console at a different Worker API server URL. */}
        <button type="button" className="auth-token" onClick={() => setShowUrlForm((value) => !value)}>
          <LinkIcon />
          Advanced: set the Worker API URL
        </button>

        {showUrlForm && (
          <form onSubmit={handleSaveUrl} style={{ marginTop: '14px' }}>
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label htmlFor="sso-api-url">Worker API URL *</label>
              <input
                id="sso-api-url"
                type="url"
                value={apiUrl}
                onChange={(event) => setApiUrl(event.target.value)}
                placeholder={
                  import.meta.env.DEV ? 'Same-origin local proxy' : 'https://your-worker.workers.dev'
                }
                required={!import.meta.env.DEV}
                autoComplete="url"
              />
            </div>
            <button type="submit" className="btn btn-secondary btn-block">
              Save API URL
            </button>
          </form>
        )}

        <div className="auth-note">
          <LockIcon />
          Session tokens are stored in this browser tab only
        </div>
      </AuthShell>
    );
  }

  const profile = auth.user?.profile;
  const userEmail = typeof profile?.email === 'string' ? profile.email : undefined;
  const userName =
    (typeof profile?.name === 'string' && profile.name) ||
    (typeof profile?.given_name === 'string' && profile.given_name) ||
    undefined;

  return (
    <SessionActionProvider
      value={{
        onAction: handleSignOut,
        actionLabel: 'Sign out',
        actionTitle: billableLocked
          ? 'Sign-out is locked while a billable request is processing.'
          : 'Sign out',
        disabled: billableLocked,
        userName,
        userEmail,
      }}
    >
      {children}
    </SessionActionProvider>
  );
}

export default SsoGate;
